import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ContactField from '@/components/ContactField';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Adding one vendor by hand (PRD §F1).
 *
 * Lifted out of the rolodex page because the composer needs it too: a planner
 * who opens a category and finds the vendor they meant to add missing should
 * not have to leave a half-picked batch to go and add it. One dialog, so the
 * duplicate check and the required fields cannot drift between the two places
 * a vendor is born.
 */

export default function AddVendorDialog({
  open,
  onOpenChange,
  defaultCategory,
  defaultBusinessName,
  defaultEmail,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Pre-picked category, for the callers that already know it — opening this
   *  from a Florist batch is not a moment to make the planner say "Florist". */
  defaultCategory?: string;
  /** Prefill, for callers that already have the planner's typing — the
   *  recipient field's "Add …" row should not make them say it twice. */
  defaultBusinessName?: string;
  defaultEmail?: string;
  /** Fired with the created vendor, so a caller can select it immediately. */
  onCreated?: (vendor: { id: string }) => void;
}) {
  const queryClient = useQueryClient();

  // The dialog's own query rather than a prop: both callers would otherwise
  // pass the same list, and one of them would eventually pass a stale one.
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return (await res.json()) as { id: string; name: string }[];
    },
    enabled: open,
  });

  const blank = {
    businessName: defaultBusinessName ?? '',
    contactName: '',
    email: defaultEmail ?? '',
    phone: '',
    website: '',
    primaryCategory: defaultCategory ?? '',
    notes: '',
  };

  const [form, setForm] = useState(blank);

  /**
   * What the contact suggestions are being asked about — whichever of the two
   * contact fields she last typed in, held apart from the form so that both
   * fields read one list. Which field she started in should not decide what she
   * can find: she may know the florist's name or she may know half their
   * address, and either way the answer is the same florist.
   */
  const [contactQuery, setContactQuery] = useState('');

  /**
   * Debounced only to avoid a request per keystroke — the endpoint is a single
   * indexed query against her own contacts and needs no protecting. 150ms is
   * under the ~200ms at which a list stops feeling like it is keeping up.
   *
   * Declared above the reset effect because that effect clears it too.
   */
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(contactQuery), 150);
    return () => clearTimeout(timer);
  }, [contactQuery]);

  // Reopening starts clean — including back on the caller's category, which a
  // planner who cleared it last time did not mean to clear forever.
  useEffect(() => {
    if (open) {
      setForm(blank);
      setDuplicates([]);
      setContactQuery('');
      // Reset alongside `contactQuery`, not left to the debounce. Otherwise
      // the live query key is still last session's text for 150ms, served
      // straight from cache, and `placeholderData` then carries that answer
      // across to the empty key — so reopening and clicking a field quickly
      // shows suggestions for a word that is no longer in the box.
      setDebouncedQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategory, defaultBusinessName, defaultEmail]);

  /**
   * The people she has already emailed, ranked and narrowed server-side.
   *
   * No Gmail connection and a send-only grant both answer `{ contacts: [] }`
   * with a 200, so there is no "not connected" branch to write here — the field
   * simply never has anything to show. A *failure* has to land in the same
   * place: a typeahead that raises a toast per keystroke is worse than one that
   * suggests nothing, so this retries nothing and resolves to no contacts.
   */
  const { data: suggestions } = useQuery({
    queryKey: ['gmail-contacts', debouncedQuery],
    queryFn: async () => {
      const res = await client.api['gmail-contacts'].$get({ query: { q: debouncedQuery } });
      if (!res.ok) throw new Error('Failed to load contacts');
      return res.json();
    },
    // Not merely `open`: an empty query asks the server for the top of its
    // ranking, and since this field only opens on typing there is nothing that
    // would ever show it. Fetching it anyway was worse than wasteful — it
    // parked that ranking in the cache under the `''` key, where the global
    // five-minute `staleTime` kept it fresh, and `placeholderData` then carried
    // it forward as the answer to her first real keystroke. She typed "b" and
    // got her family and an accountant.
    enabled: open && debouncedQuery !== '',
    retry: false,
    // Keeps the previous answer on screen while the next one is in flight.
    // Without it `data` drops to undefined between debounced queries, and the
    // dropdown she is reading blinks out and back every 150ms as she types.
    placeholderData: (previous) => previous,
  });
  const contacts = suggestions?.contacts ?? [];
  const scanning = suggestions?.scanning ?? false;

  /**
   * Asks for a scan on *every* open, not once per session.
   *
   * The watermark on the server already keeps this to one real scan a day, and
   * the asymmetry matters the other way: a server restart part-way through a
   * scan leaves `scanning: false, scannedAt: null`, which is indistinguishable
   * from never having scanned. Refreshing on each open is what heals that.
   * Fire-and-forget, and a failure is swallowed — her first keystrokes search
   * whatever has already landed, and this dialog has nothing to say about a
   * background job that did not start.
   */
  useEffect(() => {
    if (!open) return;
    void client.api['gmail-contacts'].refresh.$post().catch(() => {});
  }, [open]);

  /**
   * PRD §F1: duplicate detection warns *before* a second record exists. Fired
   * on blur rather than per keystroke — a warning that flickers while you type
   * is noise, and this one is meant to make you stop and look.
   */
  const [duplicates, setDuplicates] = useState<{ id: string; businessName: string }[]>([]);

  /**
   * `over` exists for the one caller that cannot wait for the form state it is
   * about to set: accepting a contact suggestion. Without it this reads
   * `form.email` out of its closure and checks the half-typed "bloom" the
   * suggestion just replaced.
   */
  const checkDuplicate = async (over?: { email?: string; name?: string }) => {
    const email = (over?.email ?? form.email).trim();
    const name = (over?.name ?? form.businessName).trim();
    if (!email && !name) return;
    const res = await client.api.vendors['check-duplicate'].$get({
      query: { email, name },
    });
    if (!res.ok) return;
    const data = await res.json();
    setDuplicates(data.duplicates.map((d) => ({ id: d.id, businessName: d.businessName })));
  };

  /**
   * A suggestion accepted, from the field named by `from`. Both halves of the
   * contact land at once, which is the whole point — she was going to go and
   * look the address up otherwise.
   *
   * `from` is what separates a business name she *authored* from one that is
   * merely the three letters she typed to find this contact. Accepting from the
   * business-name field replaces that field's text, because the text was the
   * query; accepting from the email field leaves it, because her own words for
   * a vendor outrank the display name on an email header — as likely to be
   * "Ava (Bloom)" as the trading name. Either way a blank name takes the
   * display name, and stays blank when the contact has none.
   *
   * The address is always applied. A suggestion row's content *is* an email
   * address: pressing one and not getting it would be the surprise, whichever
   * field the press came from.
   *
   * The duplicate check fires here rather than waiting for a blur, because
   * accepting a known address is exactly the moment the warning is most useful
   * — and it has to be told what was accepted, since `form` does not hold it
   * until the next render.
   */
  const pickContact =
    (from: 'businessName' | 'email') =>
    (contact: { email: string; displayName: string | null }) => {
      const keepTyped = from === 'email' && form.businessName.trim() !== '';
      const businessName = keepTyped
        ? form.businessName
        : (contact.displayName ?? form.businessName);
      setForm({ ...form, email: contact.email, businessName });
      void checkDuplicate({ email: contact.email, name: businessName });
    };

  const create = useMutation({
    mutationFn: async () => {
      const res = await client.api.vendors.$post({
        json: {
          businessName: form.businessName.trim(),
          contactName: form.contactName.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website.trim() || null,
          primaryCategory: form.primaryCategory || null,
          notes: form.notes.trim() || null,
          secondaryCategories: [],
          emails: form.email.trim() ? [form.email.trim()] : [],
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to create vendor');
      }
      return res.json();
    },
    onSuccess: (vendor) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      // The contacts too, because this vendor's address just stopped being a
      // suggestion: `searchContacts` leaves out every address already in the
      // rolodex, but the answer it gave before this create is cached and fresh
      // for five minutes. Without this, retyping the name she just filed shows
      // her the new vendor row *and* a "not in your rolodex yet" row for the
      // same person — which is exactly the doubling the exclusion exists to
      // prevent.
      queryClient.invalidateQueries({ queryKey: ['gmail-contacts'] });
      onOpenChange(false);
      // Not a plain `setForm(blank)`: `blank.businessName`/`blank.email` come
      // from defaultBusinessName/defaultEmail, which are one-shot text the
      // planner typed once and just spent on creating this vendor, so they
      // reset to '' instead of falling through from `blank`. `primaryCategory`
      // is left to fall through, because defaultCategory is the opposite kind
      // of prop — a standing preference for whoever opened this dialog (a
      // Florist batch stays a Florist batch no matter how many vendors get
      // added to it), not text that gets "used up" by one create. DialogContent
      // stays mounted through its ~200ms close animation (`ui/dialog.tsx`), so
      // re-seeding businessName/email from `blank` here would show the
      // just-submitted name and email fading out instead of the form actually
      // clearing.
      setForm({ ...blank, businessName: '', email: '' });
      setDuplicates([]);
      // Ahead of the toast on purpose: both calls report the same event, and
      // if a caller's onCreated throws, the success toast should not fire
      // either — it would be telling the planner the hand-off worked when it
      // didn't. (This is not about timing: the whole callback runs
      // synchronously in one tick, and toast.success renders nothing of this
      // component, so the two calls would land in the same frame either way.)
      onCreated?.(vendor);
      toast.success('Vendor added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        // The suggestion list portals to <body>, so to Radix every click on a
        // suggestion is an outside press and dismisses the dialog under the
        // planner's cursor. It is not outside in any sense she would
        // recognise, so the press is excused by where it actually landed.
        //
        // Not testable from jsdom: `fireEvent.click` never dispatches
        // `pointerdown`, so every suggestion test below stays green with or
        // without this. Hence the comment rather than a test.
        onPointerDownOutside={(e) => {
          if ((e.target as Element | null)?.closest('[data-slot="combobox-content"]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if ((e.target as Element | null)?.closest('[data-slot="combobox-content"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Add vendor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="businessName">Business name *</Label>
            <ContactField
              id="businessName"
              autoFocus
              value={form.businessName}
              onBlur={() => checkDuplicate()}
              onValueChange={(next) => {
                setForm({ ...form, businessName: next });
                setContactQuery(next);
              }}
              contacts={contacts}
              scanning={scanning}
              onPick={pickContact('businessName')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="contactName">Contact name</Label>
              <Input
                id="contactName"
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <ContactField
                id="email"
                type="email"
                value={form.email}
                onBlur={() => checkDuplicate()}
                onValueChange={(next) => {
                  setForm({ ...form, email: next });
                  setContactQuery(next);
                }}
                contacts={contacts}
                scanning={scanning}
                onPick={pickContact('email')}
              />
            </div>
          </div>

          {duplicates.length > 0 && (
            <p
              // Announced, because this warning now has a path with no focus
              // change: accepting a suggestion runs the duplicate check while
              // focus is still in the combobox, so without a live region a
              // screen-reader user accepts a known address and is told nothing.
              // On blur the focus move used to carry it. `VendorRecipientField`
              // solves the same problem the same way.
              role="status"
              className="flex items-start gap-2 rounded-2xl bg-[var(--color-tertiary-container)] px-4 py-3 text-sm"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                You already have{' '}
                {duplicates.map((d, i) => (
                  <span key={d.id}>
                    {i > 0 && ', '}
                    <Link to={`/vendors/${d.id}`} className="font-medium underline underline-offset-2">
                      {d.businessName}
                    </Link>
                  </span>
                ))}
                . Adding this creates a second record.
              </span>
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={form.primaryCategory}
                onValueChange={(v) => setForm({ ...form, primaryCategory: v })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              placeholder="bloomfloral.com"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!form.businessName.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add vendor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
